import { Tab, TabList, TabPanel, TabPanels, Tabs } from "@carbon/react";
import { TabListProps, TabPanelProps } from "@carbon/react/lib/components/Tabs/Tabs";
import { FC, ReactNode, useEffect, useState } from "react";

import {
  SelectedTabType,
  TabType,
  useTabNavigation,
  UseTabNavigationProps,
} from "src/components/TabbedView/useTabNavigation.ts";
import { cn } from "src/utils/string.utils.ts";

export type TabbedViewProps = {
  type: "flat" | "contained";
  onNavigate?: (tab: SelectedTabType) => void;
  tabs: (TabType & {
    node: ReactNode;
  })[];
  isStateful?: boolean;
  tabListProps?: Partial<TabListProps>;
  tabPanelProps?: Partial<TabPanelProps>;
  initialTabCode?: string;
} & Partial<Pick<UseTabNavigationProps, "pathPrefix">>;

// TODO: finish this component and move it to shared libs
export const TabbedView: FC<TabbedViewProps> = ({
  type,
  isStateful = true,
  pathPrefix,
  onNavigate,
  tabs,
  tabListProps,
  tabPanelProps,
  initialTabCode,
}) => {
  const { navigateTab: navigateStatefulTab, selectedTab: statefulSelectedTab } = useTabNavigation({
    tabs,
    pathPrefix,
    skip: !isStateful,
    initialTabCode,
  });

  const [notStatefulSelectedTab, setNotStatefulSelectedTab] = useState<
    SelectedTabType | undefined
  >();

  const onTabChange = ({ selectedIndex }: { selectedIndex: number }) => {
    if (isStateful) {
      navigateStatefulTab(selectedIndex);
      return;
    }
    const tab = tabs[selectedIndex] ?? tabs[0];
    setNotStatefulSelectedTab({ ...tab, index: selectedIndex });
  };

  const selectedTab = isStateful ? statefulSelectedTab : notStatefulSelectedTab;

  useEffect(() => {
    selectedTab && onNavigate && onNavigate(selectedTab);
  }, [onNavigate, selectedTab]);

  return (
    <Tabs selectedIndex={selectedTab?.index ?? 0} onChange={onTabChange}>
      <TabList
        aria-label={tabListProps?.["aria-label"] ? tabListProps["aria-label"] : "List of tabs"}
        className={tabListProps?.className}
        {...tabListProps}
        contained={type == "contained"}
      >
        {tabs.map((tab) => (
          <Tab key={tab.code}>{tab.label}</Tab>
        ))}
      </TabList>
      <TabPanels>
        {tabs.map((tab) => (
          <TabPanel
            className={cn("overflow-auto flex-[1_1_0]", tabPanelProps?.className)}
            key={tab.code}
            {...tabPanelProps}
          >
            {tab.node}
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
};
